import type { ReactNode } from 'react';
import { env } from '@/config/env';
import { useI18n } from '@/i18n/context';
import { Icon, type IconKey } from './Icon';
import styles from './ui.module.css';

/* ------------------------------------------------------------------ */
/* StatusTag — the mandatory honesty labels from the brief            */
/* DEMO · PLACEHOLDER · INTEGRATION READY · REQUIRES API ·            */
/* REQUIRES CREDENTIALS · REQUIRES BUSINESS CONFIRMATION · SAMPLE     */
/* ------------------------------------------------------------------ */
export type StatusKind =
  | 'sample'
  | 'demo'
  | 'placeholder'
  | 'integrationReady'
  | 'requiresApi'
  | 'requiresCredentials'
  | 'requiresBusinessConfirmation'
  | 'developmentVisual'
  /** Figures taken from the owner's prototype, not yet approved specifications (owner instruction 2026-09-25). */
  | 'prototypeReference';

/**
 * Inline approval marker. Hidden in staging review mode (one global ribbon
 * replaces them) unless `persistent` — e.g. the contract-scope labels that
 * must always stay visible (CLAUDE.md §3).
 */
export function StatusTag({ kind, tone = 'light', persistent = false }: { kind: StatusKind; tone?: 'light' | 'dark'; persistent?: boolean }) {
  const { t } = useI18n();
  if (env.reviewMode && !persistent) return null;
  return (
    <span className={[styles.statusTag, styles[`status_${kind}`], tone === 'dark' ? styles.statusDark : ''].join(' ')}>
      {t.common.status[kind]}
    </span>
  );
}

/* ------------------------------------------------------------------ */
export type NoticeTone = 'info' | 'success' | 'warning' | 'danger';

const NOTICE_ICONS: Record<NoticeTone, IconKey> = {
  info: 'info',
  success: 'check',
  warning: 'alert',
  danger: 'alert',
};

export function Notice({
  tone = 'info',
  title,
  children,
  action,
}: {
  tone?: NoticeTone;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className={[styles.notice, styles[`notice_${tone}`]].join(' ')} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon name={NOTICE_ICONS[tone]} size={18} className={styles.noticeIcon} />
      <div className={styles.noticeBody}>
        {title && <p className={styles.noticeTitle}>{title}</p>}
        {children && <div className={styles.noticeText}>{children}</div>}
      </div>
      {action && <div className={styles.noticeAction}>{action}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
export function Spinner({ label }: { label?: string }) {
  const { t } = useI18n();
  return (
    <span className={styles.spinnerWrap} role="status">
      <span className={styles.spinner} aria-hidden="true" />
      <span className="visually-hidden">{label ?? t.common.loading}</span>
    </span>
  );
}

export function Skeleton({ height = 16, width = '100%', radius = 6 }: { height?: number; width?: number | string; radius?: number }) {
  return <span className={styles.skeleton} style={{ blockSize: height, inlineSize: width, borderRadius: radius }} aria-hidden="true" />;
}

export function PageLoader() {
  return (
    <div className={styles.pageLoader}>
      <Spinner />
    </div>
  );
}

/* ------------------------------------------------------------------ */
export function EmptyState({ icon = 'box', title, body, action }: { icon?: IconKey; title: string; body?: string; action?: ReactNode }) {
  return (
    <div className={styles.empty}>
      <span className={styles.emptyIcon}>
        <Icon name={icon} size={22} />
      </span>
      <p className={styles.emptyTitle}>{title}</p>
      {body && <p className={styles.emptyBody}>{body}</p>}
      {action}
    </div>
  );
}

/* ------------------------------------------------------------------ */
export function PageHeader({
  title,
  subtitle,
  actions,
  tags,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  tags?: ReactNode;
}) {
  return (
    <header className={styles.pageHeader}>
      <div className={styles.pageHeaderText}>
        <div className={styles.pageHeaderTitleRow}>
          <h1 className={styles.pageTitle}>{title}</h1>
          {tags}
        </div>
        {subtitle && <p className={styles.pageSubtitle}>{subtitle}</p>}
      </div>
      {actions && <div className={styles.pageHeaderActions}>{actions}</div>}
    </header>
  );
}

/* ------------------------------------------------------------------ */
export function Panel({ title, actions, children, padded = true }: { title?: string; actions?: ReactNode; children: ReactNode; padded?: boolean }) {
  return (
    <section className={styles.panel}>
      {(title || actions) && (
        <div className={styles.panelHeader}>
          {title && <h2 className={styles.panelTitle}>{title}</h2>}
          {actions}
        </div>
      )}
      <div className={padded ? styles.panelBody : undefined}>{children}</div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
export type BadgeTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return <span className={[styles.badge, styles[`badge_${tone}`]].join(' ')}>{children}</span>;
}
